import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Youtube, MapPin, Phone, Mail, Lock } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";

const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com/drivenowautohaus", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/drivenow5401/", label: "Instagram" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Youtube, href: "https://www.youtube.com/@DriveNowAutohaus", label: "YouTube" },
];

const DARK_LOGO = "/dark_nav_logo.png";
const LIGHT_LOGO = "/light_nav_logo.png";

// Logo display size (px). Tweak these to enlarge/shrink the footer logo.
// Height is the main control; width is auto unless FOOTER_LOGO_WIDTH is set.
const FOOTER_LOGO_HEIGHT = 150;
const FOOTER_LOGO_WIDTH = null; // e.g. 140, or null for auto (keeps aspect ratio)


export default function Footer() {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  const logoSrc = isDark ? DARK_LOGO : LIGHT_LOGO;

  const footerLinks = {
    inventory: [
      { name: t('footer.allVehicles'), href: "/inventory" },
      { name: "Toyota", href: "/inventory?make=Toyota" },
      { name: "Honda", href: "/inventory?make=Honda" },
      { name: "Hyundai", href: "/inventory?make=Hyundai" },
    ],
    company: [
      { name: t('footer.aboutUs'), href: "/" },
      { name: t('footer.whyChooseUs'), href: "/#why-choose" },
      { name: t('nav.contact'), href: "/#contact" },
      { name: t('footer.careers'), href: "/" },
    ],
    services: [
      { name: t('nav.financing'), href: "/financing" },
      { name: t('nav.tradeIn'), href: "/trade-in" },
      { name: t('footer.serviceCenter'), href: "/#" },
      { name: t('footer.warranty'), href: "/#" },
    ]
  };

  const accentCls = isDark ? 'text-blue-500' : 'text-red-600';
  const linkCls = isDark
    ? 'text-gray-400 hover:text-white transition-colors text-sm'
    : 'text-gray-500 hover:text-gray-900 transition-colors text-sm';

  return (
    <footer
      className={`border-t transition-colors duration-300 ${
        isDark
          ? 'bg-[#0A0A0A] border-white/10'
          : 'bg-white border-gray-200'
      }`}
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src={logoSrc}
                alt="Drive Now Autohaus Logo"
                className="object-contain shrink-0"
                style={{
                  height: FOOTER_LOGO_HEIGHT,
                  width: FOOTER_LOGO_WIDTH ?? "auto",
                }}
              />
              <div>
                <span className={`font-unbounded font-bold text-lg tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  DRIVE NOW
                </span>
                <span className="block text-[10px] text-gray-500 uppercase tracking-[0.3em] -mt-1">
                  Autohaus
                </span>
              </div>
            </Link>
            <p className={`text-sm max-w-sm mb-6 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('footer.description')}
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a href="#" className={`${linkCls} flex items-center gap-3`}>
                <MapPin className={`h-4 w-4 ${accentCls}`} />
                5401 W Cermak Road, Cicero, IL 60804
              </a>
              <a href="tel:+17082226100" className={`${linkCls} flex items-center gap-3`}>
                <Phone className={`h-4 w-4 ${accentCls}`} />
                (708) 222-6100
              </a>
              <a href="mailto:sales@drivenow.com" className={`${linkCls} flex items-center gap-3`}>
                <Mail className={`h-4 w-4 ${accentCls}`} />
                sales@drivenow.com
              </a>
            </div>
          </div>

          {/* Inventory Links */}
          <div>
            <h4 className={`font-unbounded font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('footer.inventory')}</h4>
            <ul className="space-y-3">
              {footerLinks.inventory.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className={linkCls}>{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className={`font-unbounded font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('footer.company')}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => {
                const hashIndex = link.href.indexOf("#");
                const to =
                  hashIndex >= 0
                    ? { pathname: "/", hash: link.href.slice(hashIndex) }
                    : link.href;
                return (
                  <li key={link.name}>
                    <Link to={to} className={linkCls}>{link.name}</Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className={`font-unbounded font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('footer.servicesTitle')}</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className={linkCls}>{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`py-6 border-t flex flex-col md:flex-row items-center justify-between gap-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Drive Now Autohaus. {t('footer.rights')}
            </p>
            <Link to="/privacy" className={linkCls}>
              {t('footer.privacyPolicy')}
            </Link>
            <Link to="/terms" className={linkCls}>
              {t('footer.termsAndConditions')}
            </Link>
            <Link
              to="/admin/login"
              className={`flex items-center gap-2 px-4 py-2 border transition-all text-sm ${
                isDark
                  ? 'bg-white/5 hover:bg-white/10 border-white/10 text-gray-400 hover:text-white'
                  : 'bg-gray-100 hover:bg-gray-200 border-gray-200 text-gray-500 hover:text-gray-900'
              }`}
              data-testid="admin-login-link"
            >
              <Lock className="w-4 h-4" />
              <span className="font-['Manrope']">{t('footer.adminSignIn')}</span>
            </Link>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                {...(social.href !== "#"
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                className={`w-10 h-10 flex items-center justify-center transition-all ${
                  isDark
                    ? 'bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-900'
                }`}
                aria-label={social.label}
              >
                <social.icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
